// Pure filter+sort used by StockContext's getFilteredStocks() -- extracted
// (and kept axios-free) so the sort comparator/filter predicate can be unit
// tested against a fixed fixture without mounting the full provider or
// pulling in axios's ESM build, which this repo's CRA/Jest pin cannot
// transform from node_modules. No behavior change: this is the same logic
// that lived inline in getFilteredStocks before the "Two test files for
// 20,588 frontend lines" tech-debt ticket (2026-09).
export function filterAndSortStocks(stocks, filters) {
  // Ensure stocks is always an array
  const stocksArray = Array.isArray(stocks) ? stocks : [];
  let filtered = [...stocksArray];

  // Apply search filter
  if (filters.search) {
    filtered = filtered.filter(stock =>
      stock.symbol.toLowerCase().includes(filters.search.toLowerCase()) ||
      stock.name.toLowerCase().includes(filters.search.toLowerCase())
    );
  }

  // Apply flagged filter
  if (filters.flaggedOnly) {
    filtered = filtered.filter(stock => stock.flagged);
  }

  // Calls/Puts pills act as sort modifiers, not filters, and override any column sort:
  //   only calls  → OVR desc (highest score first)
  //   only puts   → OVR asc  (lowest score first)
  //   both/neither → OVR desc (highest score first)
  const callsActive = !!filters.callsOnly;
  const putsActive = !!filters.putsOnly;
  const pillExclusive = callsActive !== putsActive;

  if (pillExclusive) {
    const ovrOf = (s) => s.latest_score?.OVR ?? 50;
    if (callsActive) {
      filtered.sort((a, b) => ovrOf(b) - ovrOf(a));
    } else {
      filtered.sort((a, b) => ovrOf(a) - ovrOf(b));
    }
  } else if (!filters.sortBy || filters.sortBy === 'default') {
    const ovrOf = (s) => s.latest_score?.OVR ?? -1;
    filtered.sort((a, b) => ovrOf(b) - ovrOf(a));
  } else {
    // Apply explicit sorting for other fields
    filtered.sort((a, b) => {
      let aValue, bValue;

      if (filters.sortBy === 'updated_at') {
        const av = a.updated_at ? new Date(a.updated_at).getTime() : 0;
        const bv = b.updated_at ? new Date(b.updated_at).getTime() : 0;
        return filters.sortOrder === 'asc' ? av - bv : bv - av;
      }

      // Earnings days: sooner-first for desc, later-first for asc; stocks
      // with no upcoming earnings (null/negative) always pushed to the end.
      if (filters.sortBy === 'earnings') {
        const aRaw = a.next_earnings;
        const bRaw = b.next_earnings;
        const aInvalid = aRaw === null || aRaw === undefined || aRaw < 0;
        const bInvalid = bRaw === null || bRaw === undefined || bRaw < 0;
        if (aInvalid && bInvalid) return 0;
        if (aInvalid) return 1;
        if (bInvalid) return -1;
        if (aRaw !== bRaw) {
          return filters.sortOrder === 'desc' ? aRaw - bRaw : bRaw - aRaw;
        }
        // Same day: tie-break on call_time so BMO sorts before AMC under asc.
        // Stored as 'HH:MM:SS' strings so lex compare works. Missing time
        // falls to the end of its day group.
        const aCT = a.next_earnings_call_time || '99:99:99';
        const bCT = b.next_earnings_call_time || '99:99:99';
        if (aCT === bCT) return 0;
        return filters.sortOrder === 'desc'
          ? (aCT < bCT ? -1 : 1)
          : (aCT < bCT ? 1 : -1);
      }

      // Handle different field types
      if (['OVR', 'TREND', 'RSI', 'MACD', 'BB', 'X', 'VOL'].includes(filters.sortBy)) {
        // Score fields
        aValue = a.latest_score?.[filters.sortBy] || 0;
        bValue = b.latest_score?.[filters.sortBy] || 0;
      } else if (filters.sortBy === 'symbol') {
        // Symbol field (string)
        aValue = (a.symbol || '').toLowerCase();
        bValue = (b.symbol || '').toLowerCase();
      } else if (filters.sortBy === 'current_price') {
        // Price field
        aValue = a.current_price || 0;
        bValue = b.current_price || 0;
      } else if (filters.sortBy === 'pe') {
        // PE ratio field
        aValue = a.pe || 0;
        bValue = b.pe || 0;
      } else if (filters.sortBy === 'growth') {
        aValue = a.growth_metric_raw ?? -999;
        bValue = b.growth_metric_raw ?? -999;
      } else if (filters.sortBy === 'growth_rate') {
        aValue = a.growth_rate_metric_raw ?? -999;
        bValue = b.growth_rate_metric_raw ?? -999;
      } else if (filters.sortBy === 'target') {
        // Target percentage field (numeric)
        aValue = a.target_percentage_raw || 0;
        bValue = b.target_percentage_raw || 0;
      } else {
        // Default to score fields
        aValue = a.latest_score?.[filters.sortBy] || 0;
        bValue = b.latest_score?.[filters.sortBy] || 0;
      }

      if (filters.sortBy === 'symbol') {
        // String comparison for symbol only
        if (filters.sortOrder === 'desc') {
          return bValue.localeCompare(aValue);
        } else {
          return aValue.localeCompare(bValue);
        }
      } else {
        // Numeric comparison for all other fields
        if (filters.sortOrder === 'desc') {
          return bValue - aValue;
        } else {
          return aValue - bValue;
        }
      }
    });
  }

  return filtered;
}
